import { AnalyticsEvent, Job } from '@cibli/shared';
import { useEffect, useRef, useState } from 'react';

import ConfirmBeContactedDialog from '@/features/matching/components/ConfirmBeContactedDialog';
import { useUserJobApplications } from '@/hooks/useUserJobApplications';
import { useTrackEvent } from '@/lib/analytics/use-track-event.ts';

import { useBeContacted } from '../hooks/useBeContacted';
import JobDetailsScreen from './JobDetailsScreen';
import JobsListScreen from './JobsListScreen';

interface JobsState {
  jobs: Job[];
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  loading: boolean;
  streamedJobs: Job[];
  hasSourceError: boolean;
  onLoadMore: () => void;
}

interface Props {
  cvId: string;
  onCancel: () => void;
  onBack: () => void;
  matchingJobsState: JobsState;
}

export default function MatchingJobsScreen({ cvId, onCancel, onBack, matchingJobsState }: Props) {
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [showConfirmContactDialog, setShowConfirmContactDialog] = useState(false);
  const scrollPositionRef = useRef<number>(0);
  const { request: askToBeContacted } = useBeContacted();
  const hasRendered = useRef(false);
  const trackEvent = useTrackEvent();
  const { hasAppliedToJob, refetch: refetchApplications } = useUserJobApplications();

  useEffect(() => {
    if (hasRendered.current) return;
    hasRendered.current = true;
    trackEvent({ event: AnalyticsEvent.JOBS_LIST_SCREEN_RENDERED });
  }, [trackEvent]);

  const handleAskToBeContacted = async () => {
    await askToBeContacted();
    setShowConfirmContactDialog(true);
  };

  const handleJobClick = (job: Job) => {
    scrollPositionRef.current = window.scrollY;
    setSelectedJob(job);
  };

  const handleBack = () => {
    setSelectedJob(null);
    setTimeout(() => {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: 'instant',
      });
    }, 0);
  };

  return (
    <>
      {!selectedJob ? (
        <JobsListScreen
          {...matchingJobsState}
          onLoadMore={matchingJobsState.onLoadMore}
          onJobClick={handleJobClick}
          onCancel={onCancel}
          onBack={onBack}
          onAskedToBeContacted={handleAskToBeContacted}
          hasAppliedToJob={hasAppliedToJob}
          minTimePassed={minTimePassed}
          onMinTimePassed={() => setMinTimePassed(true)}
        />
      ) : (
        <JobDetailsScreen
          job={selectedJob}
          cvId={cvId}
          onBack={handleBack}
          onCancel={onCancel}
          hasApplied={hasAppliedToJob(selectedJob)}
          onApplicationSuccess={refetchApplications}
        />
      )}

      <ConfirmBeContactedDialog
        open={showConfirmContactDialog}
        onOpenChange={setShowConfirmContactDialog}
        onConfirm={onCancel}
      />
    </>
  );
}
